/**
 * Axios 请求封装
 */
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { showMessage } from '@/utils/messageProvider'
import i18n from '@/locales'

export interface RequestError extends AxiosError {
  __messageShown?: boolean
}

const SILENT_ERROR_HEADER = 'X-Silent-Error'

const isSilentRequest = (error: AxiosError): boolean => {
  const headers = error.config?.headers as Record<string, unknown> | undefined
  if (!headers) {
    return false
  }

  return headers[SILENT_ERROR_HEADER] === '1' || headers[SILENT_ERROR_HEADER.toLowerCase()] === '1'
}

let isRedirectingToLogin = false

const clearPersistedAuth = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('auth-storage')
}

const attachInterceptors = (client: AxiosInstance) => {
  // 请求拦截器
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      let token: string | null = null

      // 方式1: 优先从 localStorage.getItem('token') 读取（authStore 同步的）
      token = localStorage.getItem('token')

      // 方式2: 如果方式1没有，尝试从 Zustand persist 存储中读取
      if (!token) {
        const authStorage = localStorage.getItem('auth-storage')
        if (authStorage) {
          try {
            const parsed = JSON.parse(authStorage)
            token = parsed?.state?.token || null
          } catch (e) {
            console.error('[request] Failed to parse auth-storage:', e)
          }
        }
      }

      // console.log('[request] Token from storage:', token ? 'EXISTS' : 'NULL')

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
        // console.log('[request] Authorization header set:', token.substring(0, 20) + '...')
      } else {
        // console.warn('[request] No token found, request may fail if authentication is required')
      }

      return config
    },
    (error: AxiosError) => {
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // console.log('[response] Success:', {
      //   url: response.config.url,
      //   status: response.status,
      //   dataType: typeof response.data,
      //   dataKeys: response.data ? Object.keys(response.data) : 'null',
      //   data: response.data
      // })
      const result = response.data
      // console.log('[response] Returning:', result)
      return result
    },
    (error: AxiosError) => {
      console.error('[request] Error intercepted:', error)

      const requestError = error as RequestError
      const silentRequest = isSilentRequest(error)

      if (error.response) {
        const status = error.response.status
        const errorData = error.response.data as any

        // 优先使用 error_code 走 i18n 翻译
        let errorMsg: string | undefined
        if (errorData?.error_code) {
          // i18n.t 对缺失 key 会返回去掉命名空间后的 key（如 error.xxx），
          // 与传入的完整 key（common:error.xxx）不相等，不能用相等判断兜底。
          // 必须用 i18n.exists 判定，否则缺失翻译时会把生 key 当消息显示。
          const key = `common:error.${errorData.error_code}`
          errorMsg = i18n.exists(key)
            ? i18n.t(key, errorData.params || {})
            : errorData?.detail
        } else {
          errorMsg = errorData?.detail || errorData?.message || errorData?.error
        }

        console.error('[request] Error response:', errorMsg)

        if (silentRequest && status !== 401) {
          return Promise.reject(requestError)
        }

        switch (status) {
          case 401:
            showMessage.error(errorMsg || '登录已过期，请重新登录')
            clearPersistedAuth()

            // 已经在登录页时不再整页刷新，避免陷入重复跳转。
            if (window.location.pathname !== '/login' && !isRedirectingToLogin) {
              isRedirectingToLogin = true
              setTimeout(() => {
                window.location.replace('/login')
              }, 1000)
            }
            break
          case 403:
            showMessage.error(errorMsg || '禁止访问')
            break
          case 404:
            showMessage.error(errorMsg || '请求的资源不存在')
            break
          case 422:
            // 表单验证错误，显示具体错误信息
            showMessage.error(errorMsg || '参数验证失败')
            break
          case 409:
            // 409 表示业务规则不满足（如周度指标缺少 IRI 输入数据），
            // 属正常业务结果而非程序错误，用绿色提示而不是红色 error。
            showMessage.success(errorMsg || '当前条件不满足，无法处理该请求')
            break
          case 500:
            showMessage.error(errorMsg || '服务器错误，请稍后重试')
            break
          case 502:
          case 503:
          case 504:
            showMessage.error('服务暂时不可用，请稍后重试')
            break
          default:
            showMessage.error(errorMsg || `请求失败 (${status})`)
        }

        requestError.__messageShown = true
      } else if (error.request) {
        // 请求已发出但没有收到响应
        console.error('[request] No response received')
        if (silentRequest) {
          return Promise.reject(requestError)
        }
        showMessage.error('网络错误，请检查网络连接')
        requestError.__messageShown = true
      } else {
        // 请求配置出错
        console.error('[request] Request config error:', error.message)
        if (silentRequest) {
          return Promise.reject(requestError)
        }
        showMessage.error('请求配置错误')
        requestError.__messageShown = true
      }

      return Promise.reject(requestError)
    }
  )
}

const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000,
})

export const gapiRequest = axios.create({
  baseURL: import.meta.env.VITE_GAPI_BASE_URL || '/gapi',
  timeout: 30000,
})

attachInterceptors(request)
attachInterceptors(gapiRequest)

export default request
